import { z } from "zod";
import { actionSchema, focusModes } from "./action.js";
import type { Eta } from "./eta.js";
import { limits } from "./limits.js";
import { fpsSchema } from "./primitives.js";
import { timelineSchema } from "./timeline.js";

/** Request/response shapes shared by the API and the web portal. */

export const projectInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  tone: z.string().trim().max(120).optional(),
  audience: z.string().trim().max(120).optional(),
  purpose: z.string().trim().max(40).optional(),
  instructions: z.string().trim().max(2000).optional(),
});

export const projectIdSchema = z.object({ id: z.string().uuid() });

export const scriptUpdateSchema = z.object({
  scenes: z
    .array(
      z.object({
        id: z.string().min(1).max(64),
        script: z.string().max(4000),
        /**
         * Optional so a caller editing only words need not send it, and so a
         * client that predates pauses keeps working. Sent, it replaces the
         * silence held before this line.
         */
        pauseBefore: z.number().min(0).max(30).optional(),
      }),
    )
    .min(1)
    .max(limits.maxScenes),
  regenerateVoice: z.boolean().default(false),
});

export const voiceRequestSchema = z.object({
  voiceId: z.string().trim().max(120).optional(),
  sections: z.array(z.enum(["head", "body", "end"])).default(["head", "body", "end"]),
});

export const renderRequestSchema = z.object({
  width: z.number().int().min(320).max(3840).default(1920),
  height: z.number().int().min(240).max(2160).default(1080),
  fps: fpsSchema.default(30),
  crf: z.number().int().min(1).max(51).default(18),
});

export const timelinePutSchema = z.object({
  timeline: timelineSchema,
  /** Optional label recorded with the revision. */
  createdBy: z.string().max(120).default("editor"),
});

export const actionsIngestSchema = z.object({
  actions: z.array(actionSchema).max(limits.maxActions),
});

export const focusModeSchema = z.enum(focusModes);

export type ProjectInput = z.infer<typeof projectInputSchema>;
export type ScriptUpdate = z.infer<typeof scriptUpdateSchema>;
export type RenderRequest = z.infer<typeof renderRequestSchema>;
export type VoiceRequest = z.infer<typeof voiceRequestSchema>;

/** Pipeline steps surfaced on the processing screen, in execution order. */
export const pipelineSteps = [
  "uploaded",
  "audio-extracted",
  "transcribed",
  "script-generated",
  "actions-detected",
  "voice-generated",
  "timeline-built",
  "ready",
] as const;

export type PipelineStep = (typeof pipelineSteps)[number];

/** How a pipeline step should be drawn. */
export type PipelineStepState = "done" | "active" | "failed" | "pending";

/**
 * Maps project status plus finished steps onto a display state per step.
 *
 * The subtle part is `active`. A spinner asserts "the worker is doing this right
 * now", so it must be driven by whether a job actually exists — not merely by
 * which step is next. A `draft` project has no job at all: it is waiting on the
 * user to upload or record, and spinning on "Uploaded" tells them the opposite
 * of the truth.
 */
export function pipelineStepStates(
  status: string,
  completedSteps: readonly PipelineStep[],
): Record<PipelineStep, PipelineStepState> {
  const failed = status === "failed";
  const running = status === "uploaded" || status === "processing" || status === "rendering";
  const activeIndex = pipelineSteps.findIndex((step) => !completedSteps.includes(step));

  return Object.fromEntries(
    pipelineSteps.map((step, index) => {
      if (completedSteps.includes(step)) return [step, "done"];
      if (index !== activeIndex) return [step, "pending"];
      if (failed) return [step, "failed"];
      return [step, running ? "active" : "pending"];
    }),
  ) as Record<PipelineStep, PipelineStepState>;
}

export interface ProjectStatusResponse {
  id: string;
  status: string;
  step: PipelineStep | null;
  progress: number;
  error: string | null;
  /**
   * The `AppError.code` behind `error`, when the failure had one.
   *
   * What the screen needs in order to offer something. "Add credit to process
   * it" and "the transcription provider timed out" are both failures with the
   * same shape; only one of them has a button, and matching on the wording to
   * tell them apart breaks the first time somebody rewrites the sentence.
   */
  errorCode: string | null;
  completedSteps: PipelineStep[];
  /** Time left on the processing job; null when nothing is being processed. */
  eta?: Eta | null;
  /** The processing job, so the page and the jobs menu count it down as one. */
  jobId?: string | null;
}
